<script setup lang="ts">
import {
  EButton,
  ECheckbox,
  EDialog,
  EField,
  EInput,
  ERadioGroup,
  fieldError,
  toProblemDetails,
  useToast,
  type ProblemDetails,
} from '@erpx/ui'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { WEB_ERROR_CODES } from '@/api/client'
import { problemText } from '@/i18n/problem'
import { useReadOnly } from '@/composables/useReadOnly'
import { newIdempotencyKey } from '@/lib/idempotency'
import {
  MIN_PASSWORD_LENGTH,
  passwordPolicyKey,
  passwordPolicyViolation,
} from '@/lib/password'
import { PERMISSIONS } from '@/lib/permissions'
import { QUERY_KEYS } from '@/lib/queryKeys'
import RoleAssignmentEditor from '@/modules/users/RoleAssignmentEditor.vue'
import { inviteUser, listRoles, type CredentialMode } from '@/modules/users/api'
import { useAuthStore } from '@/stores/auth'

/**
 * Add a colleague, without leaving the page (§11 quick-create).
 *
 * Three ways in, because "email them a link" is not always available: a resort
 * back-office hire may have no mailbox for a week, and an operator standing at
 * the desk needs the account to work now. So the drawer offers the server's
 * three credential modes — invitation (the default and the historical
 * behaviour), a password the operator types, and one the server generates.
 *
 * Two things about the generated password are deliberate and load-bearing:
 *
 *  - It is shown **once**, from a component ref, and never written to
 *    localStorage, pinia or a query cache. The server does not store it in any
 *    readable form either (CLAUDE.md §10's "never shows a password" was
 *    superseded precisely here, and the compensating control is that it is
 *    unrecoverable). A lost one is replaced, not retrieved.
 *  - "Require them to change it at first sign-in" defaults to on, matching the
 *    server's own default. While it is set the user can sign in and do nothing
 *    else, which is what makes an operator knowing the password acceptable —
 *    the knowledge expires the first time they use it.
 *
 * The idempotency key is minted when the drawer opens and reused for every
 * attempt of that one intent, so a flaky connection produces one account and
 * one email, not three (§7).
 */

const open = defineModel<boolean>('open', { default: false })

const auth = useAuthStore()
const toast = useToast()
const queryClient = useQueryClient()
const { readOnly } = useReadOnly()
const { t } = useI18n()

const email = ref('')
const name = ref('')
const roleCodes = ref<string[]>([])
const overrideReason = ref('')
const problem = ref<ProblemDetails | null>(null)
const submitting = ref(false)

const credentialMode = ref<CredentialMode>('invite')
const password = ref('')
const confirmation = ref('')
const reveal = ref(false)
const mustChangePassword = ref(true)

/**
 * The one and only copy of a server-generated password.
 *
 * A plain ref, scoped to this component, cleared whenever the drawer opens or
 * closes. It is not in a store and not in a cache on purpose — anything with a
 * longer life than the dialog is somewhere a secret can be read back from.
 */
const generatedPassword = ref('')
const acknowledged = ref(false)

let idempotencyKey = newIdempotencyKey()

const canListRoles = computed(() => auth.can(PERMISSIONS.roleList))

const { data: roles, isPending: rolesPending } = useQuery({
  queryKey: QUERY_KEYS.roles,
  queryFn: listRoles,
  enabled: canListRoles,
})

/** The drawer is in one of two states: the form, or handing a secret over. */
const step = computed<'form' | 'handover'>(() =>
  generatedPassword.value === '' ? 'form' : 'handover',
)

const modeOptions = computed(() => [
  {
    value: 'invite' as CredentialMode,
    label: t('users.invite.credential.invite'),
    hint: t('users.invite.credential.inviteHint'),
  },
  {
    value: 'set' as CredentialMode,
    label: t('users.invite.credential.set'),
    hint: t('users.invite.credential.setHint'),
  },
  {
    value: 'generate' as CredentialMode,
    label: t('users.invite.credential.generate'),
    hint: t('users.invite.credential.generateHint'),
  },
])

const setsPassword = computed(() => credentialMode.value === 'set')
const carriesPassword = computed(() => credentialMode.value !== 'invite')
const inputType = computed(() => (reveal.value ? 'text' : 'password'))

const policyError = computed<string | null>(() => {
  if (!setsPassword.value || password.value === '') return null
  const violation = passwordPolicyViolation(password.value)
  if (violation === null) return null
  return t(passwordPolicyKey(violation), { min: MIN_PASSWORD_LENGTH, max: 256 })
})

const mismatch = computed(
  () => setsPassword.value && confirmation.value !== '' && confirmation.value !== password.value,
)

const passwordReady = computed(
  () =>
    !setsPassword.value ||
    (password.value !== '' && confirmation.value === password.value && policyError.value === null),
)

const conflicts = computed(() =>
  problem.value?.code === WEB_ERROR_CODES.sodViolation
    ? (problem.value.errors ?? []).map((entry) => entry.message)
    : [],
)

const HANDLED_FIELDS = ['email', 'name', 'password', 'credentialMode']

const summaryError = computed(() => {
  if (problem.value === null) return ''
  if (problem.value.code === WEB_ERROR_CODES.sodViolation) return ''
  const fields = (problem.value.errors ?? []).map((entry) => entry.field)
  const handled = fields.length > 0 && fields.every((field) => HANDLED_FIELDS.includes(field))
  return handled ? '' : problemText(problem.value)
})

function resetForm(): void {
  email.value = ''
  name.value = ''
  roleCodes.value = []
  overrideReason.value = ''
  problem.value = null
  credentialMode.value = 'invite'
  password.value = ''
  confirmation.value = ''
  reveal.value = false
  mustChangePassword.value = true
  generatedPassword.value = ''
  acknowledged.value = false
}

// Both edges matter. Opening starts a clean intent; closing is what guarantees
// the generated password does not survive in memory behind a shut dialog.
watch(open, (isOpen) => {
  resetForm()
  if (isOpen) idempotencyKey = newIdempotencyKey()
})

// Switching away from "set" drops what was typed, so a password can never be
// sent under a mode the server forbids it in — and so it is not sitting in the
// DOM under a mode that does not use it.
watch(credentialMode, () => {
  password.value = ''
  confirmation.value = ''
  reveal.value = false
})

async function submit(): Promise<void> {
  if (submitting.value || readOnly.value || !passwordReady.value) return
  submitting.value = true
  problem.value = null

  try {
    const result = await inviteUser(
      {
        email: email.value.trim(),
        name: name.value.trim(),
        roleCodes: roleCodes.value,
        overrideReason: overrideReason.value.trim(),
        credentialMode: credentialMode.value,
        ...(setsPassword.value ? { password: password.value } : {}),
        ...(carriesPassword.value ? { mustChangePassword: mustChangePassword.value } : {}),
      },
      idempotencyKey,
    )

    await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users })

    const secret = result.generatedPassword
    if (secret !== undefined && secret !== null && secret !== '') {
      // Do not close. This value exists nowhere else, in this app or on the
      // server, and closing the drawer would destroy it.
      generatedPassword.value = secret
      // The form fields are cleared here rather than on close, so nothing the
      // operator typed lingers behind the handover panel.
      password.value = ''
      confirmation.value = ''
      return
    }

    if (credentialMode.value === 'invite') {
      toast.success(t('users.invite.sent'), t('users.invite.sentDetail', { email: result.user.email }))
    } else {
      toast.success(
        t('users.invite.created'),
        t('users.invite.createdDetail', { email: result.user.email }),
      )
    }
    open.value = false
  } catch (error) {
    problem.value = toProblemDetails(error)
    if (problem.value === null) {
      toast.error(t('users.invite.failed'), problemText(error))
    }
  } finally {
    submitting.value = false
  }
}

async function copyGenerated(): Promise<void> {
  try {
    await navigator.clipboard.writeText(generatedPassword.value)
    toast.success(t('users.invite.handover.copied'), t('users.invite.handover.copiedDetail'))
  } catch {
    // A non-secure origin or a denied permission. Saying so beats a button that
    // appears to work and does nothing — the value is still on screen to read.
    toast.warning(
      t('users.invite.handover.copyBlocked'),
      t('users.invite.handover.copyBlockedDetail'),
    )
  }
}

function finishHandover(): void {
  open.value = false
}
</script>

<template>
  <EDialog
    v-model:open="open"
    variant="drawer"
    size="md"
    :persistent="step === 'handover' && !acknowledged"
    :hide-close="step === 'handover' && !acknowledged"
    :title="$t('users.invite.title')"
    :description="$t('users.invite.description')"
  >
    <!-- ------------------------------------------------- hand the secret over -->
    <div v-if="step === 'handover'" class="stack">
      <p class="alert alert--warning" role="status">{{ $t('users.invite.handover.warning') }}</p>

      <div class="stack--tight">
        <span id="generated-password-label" class="muted text-xs">
          {{ $t('users.invite.handover.label', { name }) }}
        </span>
        <!-- dir="ltr" isolates the value inside an RTL layout: a password is a
             code, and reordering it produces a different password. -->
        <div
          class="codeblock codeblock--single"
          dir="ltr"
          role="group"
          aria-labelledby="generated-password-label"
        >
          <span>{{ generatedPassword }}</span>
        </div>
      </div>

      <div class="row">
        <EButton size="sm" variant="secondary" @click="copyGenerated">
          {{ $t('common.actions.copy') }}
        </EButton>
      </div>

      <ECheckbox v-model="acknowledged" :label="$t('users.invite.handover.acknowledge')" />
    </div>

    <!-- --------------------------------------------------------------- form -->
    <form v-else id="invite-user-form" class="stack" novalidate @submit.prevent="submit">
      <p v-if="summaryError !== ''" class="alert" role="alert">{{ summaryError }}</p>

      <EField
        :label="$t('common.labels.email')"
        required
        :error="fieldError(problem, 'email') ?? null"
      >
        <EInput
          v-model="email"
          type="email"
          autocomplete="off"
          :placeholder="$t('users.invite.emailPlaceholder')"
        />
      </EField>

      <EField
        :label="$t('common.labels.fullName')"
        required
        :error="fieldError(problem, 'name') ?? null"
      >
        <EInput v-model="name" autocomplete="off" :placeholder="$t('users.invite.namePlaceholder')" />
      </EField>

      <EField
        :label="$t('users.invite.credential.legend')"
        :error="fieldError(problem, 'credentialMode') ?? null"
      >
        <ERadioGroup v-model="credentialMode" :options="modeOptions" />
      </EField>

      <template v-if="setsPassword">
        <EField
          :label="$t('common.labels.password')"
          :hint="$t('users.invite.passwordHint', { min: MIN_PASSWORD_LENGTH })"
          required
          :error="policyError ?? fieldError(problem, 'password') ?? null"
        >
          <EInput v-model="password" :type="inputType" autocomplete="new-password" />
        </EField>

        <EField
          :label="$t('common.labels.confirmPassword')"
          required
          :error="mismatch ? $t('common.validation.passwordMismatch') : null"
        >
          <EInput v-model="confirmation" :type="inputType" autocomplete="new-password" />
        </EField>

        <ECheckbox v-model="reveal" :label="$t('common.actions.showPassword')" />
      </template>

      <ECheckbox
        v-if="carriesPassword"
        v-model="mustChangePassword"
        :label="$t('users.invite.mustChange.label')"
        :description="$t('users.invite.mustChange.hint')"
      />

      <RoleAssignmentEditor
        v-if="canListRoles"
        v-model="roleCodes"
        v-model:reason="overrideReason"
        :roles="roles ?? []"
        :loading="rolesPending"
        :conflicts="conflicts"
      />

      <p v-else class="muted text-xs">{{ $t('users.invite.noRolePermission') }}</p>
    </form>

    <template #footer>
      <template v-if="step === 'handover'">
        <EButton variant="primary" :disabled="!acknowledged" @click="finishHandover">
          {{ $t('common.actions.done') }}
        </EButton>
      </template>
      <template v-else>
        <EButton variant="ghost" @click="open = false">{{ $t('common.actions.cancel') }}</EButton>
        <EButton
          type="submit"
          form="invite-user-form"
          variant="primary"
          :loading="submitting"
          :loading-label="
            carriesPassword ? $t('users.invite.submittingCreate') : $t('users.invite.submitting')
          "
          :disabled="readOnly || !passwordReady"
        >
          {{ carriesPassword ? $t('users.invite.submitCreate') : $t('users.invite.submit') }}
        </EButton>
      </template>
    </template>
  </EDialog>
</template>
